import {
  IInterviewerPreferences,
  InterviewersFilter,
  NoAvailabilityReason,
  ReasonType
} from "@rooster/types";
import { create } from "zustand";
import { DocData } from "../services/api/types/autobookAvailability.type";
import {
  DateElement,
  Participant
} from "../services/api/types/availability.type";
import { Organizer, Provider } from "../services/api/types/roosterlinks.type";
import {
  AvailabilityDocData,
  DocData as RoosterDocData
} from "../services/api/types/roostermeeting.type";
import { BookingFormType } from "../types";

type ClientType = "standalone" | "normal";
type MeetingType = "rooster" | "rooster_links";
type MeetingMode = "autobook" | "availability";
export type MeetingParticipantModelType = "1:1" | "group";
export type MeetingAvailabilityWindowType = "individual_dates" | "range";
export type MeetingAvailabilityRange = { rangeStart: string; rangeEnd: string };
type MeetingAvailabilityDates = number[];
export type MeetingProfileType = "prospect" | "standalone";

export interface InterviewerType {
  countryCode?: null | string;
  department?: null | string;
  email: string;
  employeeID?: string;
  firstName?: string;
  imageURL?: null;
  imageURL40x40?: null;
  key?: string;
  lastName?: string;
  name: string;
  telephone?: null;
  title?: null;
  type?: string | null;
  value: string;
  country?: null;
  isCandidate?: boolean;
}
export type MeetingLinkType = {
  id?: string;
  type?: MeetingType;
  mode?: MeetingMode;
  jobReqId?: string;
  hasDynamicInterviewersFilter?: boolean;
  roosterLinksMeta?: {
    email?: string;
    title?: string;
    description?: string;
    organizer?: Organizer;
    profileType?: MeetingProfileType;
    participantType?: MeetingParticipantModelType;
    candidateRequiredFields?: string[];
    termsAndConditions?: TermsAndCondition[];
  };
  availability?: {
    type: MeetingAvailabilityWindowType;
    availabilityWindowMins: number;
    range?: MeetingAvailabilityRange[];
    dates?: MeetingAvailabilityDates;
    duration: number;
    autobookIntervalFreq?: number;
  };
  interviewers?: InterviewerType[];
  interviewersFilter?: InterviewersFilter | null;
  isDisabled?: boolean;
  canCancel?: boolean;
  canReschedule?: boolean;
  rescheduleStatus?: string | null;
  showTentativeAsBusy?: boolean;
  scheduledData?: {
    form?: BookingFormType;
    docDataRooster?: DocData;
    docDataRoosterLinks?: RoosterDocData;
    availabilityDocData?: AvailabilityDocData;
    alreadyScheduled: boolean;
  };
  status?: string | null;
};
type ProvidersType = Provider[];
export type sessionValidationState =
  | "pending"
  | "validated"
  | "validation_failed";

export type SessionDataType = {
  expiredLink?: boolean;
  isRescheduleFlow?: boolean;
  requestSessionStartedAt?: number;
  validated?: sessionValidationState;
  formData?: BookingFormType;
  selectedTimezone?: string;
  selectedSlot?: {
    date: number;
    hour: number | null;
    minute: number | null;
    participants?: Participant[];
  } | null;
  prevSelectedSlot?: {
    duration?: number;
    day: number;
    hour: number;
    minute: number;
    timeZone: string;
  }[];
  // The slot the current attempt is scheduling/rescheduling FROM, snapshotted
  // for the final confirmation screen only. prevSelectedSlot is overwritten on
  // schedule success (optimistic baseline for the NEXT same-page reschedule),
  // which would otherwise make the confirmation show the just-booked slot as
  // its own "previous" (strikethrough == new). This dedicated key keeps the
  // confirmation's strikethrough correct without disturbing that baseline.
  confirmationPrevSlot?: {
    duration?: number;
    day: number;
    hour: number;
    minute: number;
    timeZone: string;
  }[];
  computedAvailability?: DateElement[];
  rescheduleReason?: {
    reason?: ReasonType;
    comment?: string;
  };
};

export type ViewType = "month" | "week" | "column" | "availability-slot";
export type UiStateType = {
  currentView: ViewType;
  currentStep:
    | "loading"
    | "initial"
    | "form"
    | "form_inline"
    | "termsandcondition"
    | "scheduling"
    | "scheduled"
    | "scheduling_failed"
    | "scheduling_cancelled"
    | "availability"
    | "failed"
    | "error"
    | "reschedules_exhausted";
  error?: {
    code?: string;
    message?: string;
    description?: string;
    type?: "persistent" | "dismissible";
    primaryButtonText?: string;
    primaryButtonAction?: () => void;
  };
  meetingCardError?: {
    code: string;
    message: string;
  };
  workDayView?: boolean;
};
export type HostType = {
  name: string;
  email: string;
  interviewerPreferences?: {
    availability: IInterviewerPreferences["availability"];
    additionalPreferences: IInterviewerPreferences["additionalPreferences"];
  };
};

export interface TermsAndCondition {
  name: string;
  description: string;
  status: string;
  type: string;
  version: number;
  content: Content[];
  _id: string;
  createdAt: string;
  updatedAt: string;
  parentId: string;
}

export interface Content {
  language: string;
  body: string;
  _id: string;
}

export type SchedulerStoreType = {
  trackingId: string;
  referer: string | undefined;
  /**
   * Tenant of the verified `internalRef` token, or null when the visitor is a
   * candidate. Holding the tenant (rather than a bare boolean) is what stops a
   * token minted by one tenant's user from suppressing another tenant's T&C.
   */
  verifiedInternalClient: string | null;
  clientType: ClientType | null;
  clientId: string | null;
  link?: {
    userId: string | null;
    linkId: string | null;
    scheduleId: string | null;
    viewMode: string | null;
  };
  user?: {
    id: string | null;
    name: string | null;
    wid: string | null;
  };
  host?: HostType;
  meetingLink?: MeetingLinkType;
  showTour?: boolean;
  providers?: ProvidersType;
  sessionData?: SessionDataType;
  ui?: UiStateType;
  loadingAvailability?: boolean;
  cleanSession?: boolean;
  clientLogo?: string;
  noAvailabilityLog?: NoAvailabilityReason;
  actions: {
    setNoAvailabilityLog: (log: NoAvailabilityReason) => void;
    setClient: (clientType: ClientType, ClientId: string) => void;
    setLink: (
      userId: string | null,
      linkId: string | null,
      scheduleId: string | null,
      viewMode: string | null
    ) => void;
    setUser: (id: string | null, name: string, wid: string) => void;
    setHost: (
      name: string,
      email: string,
      interviewerPreferences?: {
        availability: IInterviewerPreferences["availability"];
        additionalPreferences: IInterviewerPreferences["additionalPreferences"];
      }
    ) => void;
    setTermsAndConditions: (tnc: TermsAndCondition[]) => void;
    setMeetingLink: (meetingLink: MeetingLinkType) => void;
    setSessionValidation: (
      sessionValidationState: sessionValidationState
    ) => void;
    setProviders: (providers: ProvidersType) => void;
    setSessionData: (sessionData: SessionDataType) => void;
    setUiState: (uiState: UiStateType) => void;
    setUiView: (view: ViewType) => void;
    setLoadingAvailability: (loading: boolean) => void;
    setCleanSession: (state: boolean) => void;
    setShowTour: (show: boolean) => void;
    setClientLogo: (logo: string) => void;
    setTrackingId: (trackingId: string) => void;
    setReferer: (referer: string | undefined) => void;
    setVerifiedInternalClient: (client: string | null) => void;
  };
};

export const useSchedulerStore = create<SchedulerStoreType>()((set) => {
  return {
    trackingId: "",
    referer: undefined,
    verifiedInternalClient: null,
    clientType: null,
    clientId: null,
    actions: {
      setNoAvailabilityLog(log) {
        set((state) => ({
          ...state,
          noAvailabilityLog: log
        }));
      },
      setTrackingId: (trackingId) =>
        set((state) => ({
          ...state,
          trackingId
        })),
      setReferer: (referer) =>
        set((state) => ({
          ...state,
          referer
        })),
      setVerifiedInternalClient: (client) =>
        set((state) => ({
          ...state,
          verifiedInternalClient: client
        })),
      setClient: (clientType, clientId) =>
        set((state) => ({
          ...state,
          clientId,
          clientType
        })),
      setLoadingAvailability: (loading) =>
        set((state) => ({ ...state, loadingAvailability: loading })),
      setClientLogo: (logo) => set((state) => ({ ...state, clientLogo: logo })),
      setCleanSession: (isClean) =>
        set((state) => ({ ...state, cleanSession: isClean })),
      setLink: (userId, linkId, scheduleId, viewMode) =>
        set((state) => ({
          ...state,
          link: { userId, linkId, scheduleId, viewMode }
        })),
      setUser: (id, name, wid) =>
        set((state) => ({
          ...state,
          user: { id, name, wid }
        })),
      setShowTour: (show) =>
        set((state) => ({
          ...state,
          showTour: show
        })),
      setHost: (name, email, interviewerPreferences) =>
        set((state) => ({
          ...state,
          host: { name, email, interviewerPreferences }
        })),
      setMeetingLink: (meetingLink) =>
        set((state) => ({
          ...state,
          meetingLink
        })),
      setProviders: (providers) =>
        set((state) => ({
          ...state,
          providers
        })),
      setTermsAndConditions: (tnc) =>
        set((state) => ({
          ...state,
          meetingLink: {
            ...state.meetingLink,
            roosterLinksMeta: {
              ...state.meetingLink?.roosterLinksMeta,
              termsAndConditions: tnc,
              organizer: state.meetingLink?.roosterLinksMeta?.organizer
            }
          }
        })),
      setSessionData: (sessionData) => {
        set((state) => ({
          ...state,

          sessionData
        }));
      },
      setSessionValidation: (sessionValidationState) =>
        set((state) => ({
          ...state,
          sessionData: {
            ...state.sessionData,
            validated: sessionValidationState
          }
        })),
      setUiState: (ui) =>
        set((state) => ({
          ...state,
          ui
        })),
      setUiView: (view: ViewType) =>
        set((state) => ({
          ...state,
          ui: {
            ...state.ui,
            currentStep: state.ui?.currentStep ?? "initial",
            currentView: view
          }
        }))
    },
    ui: {
      currentStep: "loading",
      currentView: "month",
      workDayView: true
    },
    loadingAvailability: false,
    cleanSession: true
  };
});
